import { useState, useEffect, useRef, memo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import api from '@/services/api';

const FALLBACK = 'https://images.unsplash.com/photo-1616723355486-eac8780bfcb9?crop=entropy&cs=srgb&fm=jpg&q=85';

// Sliding photo carousel — THE Onda standard. Auto-advances through the stylist's
// approved portfolio; fetches lazily per card, falls back to legacy images then stock.
// Memoized: parent pages re-render on scroll; carousels must not repaint with them.
function PhotoCarousel({ stylistId, fallbackImages = [], alt = '', className = '' }) {
  const [photos, setPhotos] = useState(() =>
    (fallbackImages || []).filter(Boolean).map((url, i) => ({ photo_id: `fb_${i}`, url }))
  );
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    // Mock/demo placeholder cards (stylist_001…) have no server photos — don't
    // spam the console with guaranteed 404s.
    if (!stylistId || String(stylistId).startsWith('stylist_')) return undefined;
    let live = true;
    api.get(`/portfolio/stylist/${stylistId}/photos`)
      .then(({ data }) => { if (live && data.length) { setPhotos(data); setIdx(0); } })
      .catch(() => { /* fall back to whatever we have */ });
    return () => { live = false; };
  }, [stylistId]);

  useEffect(() => {
    if (photos.length < 2) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % photos.length), 3500);
    return () => clearInterval(t);
  }, [photos.length]);

  const go = (e, delta) => {
    e.stopPropagation();
    setIdx((i) => (i + delta + photos.length) % photos.length);
  };

  // Swipe support — a swipe changes the photo and must NOT count as a tile click.
  const touch = useRef({ x: 0, swiped: false });
  const onPointerDown = (e) => { touch.current = { x: e.clientX, swiped: false }; };
  const onPointerUp = (e) => {
    const dx = e.clientX - touch.current.x;
    if (photos.length > 1 && Math.abs(dx) > 40) {
      touch.current.swiped = true;
      setIdx((i) => (i + (dx < 0 ? 1 : -1) + photos.length) % photos.length);
    }
  };
  const onClickCapture = (e) => {
    if (touch.current.swiped) {
      e.stopPropagation();
      e.preventDefault();
      touch.current.swiped = false;
    }
  };

  const current = photos[idx]?.url || FALLBACK;

  return (
    <div
      className={`relative w-full h-full overflow-hidden touch-pan-y ${className}`}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onClickCapture={onClickCapture}
    >
      <img
        key={current}
        src={current}
        alt={alt}
        loading="lazy"
        draggable={false}
        className="w-full h-full object-cover object-center transition-opacity duration-500 select-none"
        onError={(e) => { e.target.src = FALLBACK; }}
      />
      {photos.length > 1 && (
        <>
          <button type="button" onClick={(e) => go(e, -1)} aria-label="Previous photo"
            className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/50 text-white/90 opacity-0 group-hover:opacity-100 hover:bg-black/70 transition-opacity">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button type="button" onClick={(e) => go(e, 1)} aria-label="Next photo"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/50 text-white/90 opacity-0 group-hover:opacity-100 hover:bg-black/70 transition-opacity">
            <ChevronRight className="h-4 w-4" />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {photos.slice(0, 8).map((p, i) => (
              <span key={p.photo_id}
                className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-4 bg-primary' : 'w-1.5 bg-white/50'}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default memo(PhotoCarousel);
